import { observable } from 'src_common/common/mobx-wrapper';
import { action, makeAutoObservable } from 'mobx';
import React, { ChangeEvent, useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { toNumber } from 'lodash';
import { UpdateTemplateBody } from 'src/components/MarketTemplateSettingsForm/MarketTemplateSettingsForm.state';

function buildObject(data: string, value: string): Record<string, Record<string, number>> | null {
    const objectKeys = data.split('.');
    if (objectKeys[0] === 'cashoutMarketSettings') {
        const base = {
            cashoutMarketSettings: {},
        };

        switch (objectKeys[1]) {
            case 'baseCashout':
                base.cashoutMarketSettings = {
                    baseCashout: Math.round(parseFloat(value) * 100),
                };
                return base;
            case 'cashoutOverround':
                base.cashoutMarketSettings = {
                    cashoutOverround: Math.round(parseFloat(value) * 100),
                };
                return base;
            case 'cashoutOfferEasing':
                base.cashoutMarketSettings = {
                    cashoutOfferEasing: Math.round(parseFloat(value) * 100),
                };
                return base;
        }
    }

    return null;
}

function shouldBeNumber(data: string): boolean {
    return data === 'inPlayDelay' || data === 'eachWayStakeReduction';
}

function shouldBeBool(inputType: string): boolean {
    return inputType === 'checkbox';
}

function isObject(data: string): boolean {
    return data.includes('.');
}

type ValidationType = {
    type: 'range';
    details: { min: number; max: number };
};

type InputPropsType = {
    dataTest: string;
    label: string;
    name: string;
    value?: string | number;
    onSave: (data: UpdateTemplateBody) => void;
    isCache?: boolean;
    validation?: ValidationType;
};
class InputState {
    @observable currentInputValue: string | number | null = null;
    constructor(
        readonly onSaveMarket: (data: UpdateTemplateBody) => void,
        readonly name: string,
        readonly validation?: ValidationType
    ) {
        makeAutoObservable(this);
    }
    @action onSave = (e: ChangeEvent<HTMLInputElement>): void => {
        const data: string | number | boolean = e.target.value;
        this.currentInputValue = data;
        if (this.isValid === false) {
            return;
        }
        if (isObject(this.name)) {
            const obj = buildObject(this.name, data);
            if (obj === null) {
                return;
            }
            this.onSaveMarket(obj);
            return;
        } else if (shouldBeNumber(this.name)) {
            this.onSaveMarket({ [this.name]: parseFloat(data) });
            return;
        } else if (shouldBeBool(e.target.type)) {
            this.onSaveMarket({ [this.name]: e.target.checked });
            return;
        }

        this.onSaveMarket({ [this.name]: data });
    };

    get isValid(): boolean {
        if (this.validation?.type === 'range') {
            const min = this.validation.details.min;
            const max = this.validation.details.max;
            // @ts-expect-error - temporary solution
            if (this.currentInputValue !== null && (this.currentInputValue < min || this.currentInputValue > max)) {
                return false;
            }
        }

        return true;
    }
}

export const Input = observer(
    'MarketTemplateInput',
    ({ dataTest, label, name, value, onSave, isCache, validation }: InputPropsType) => {
        const [state] = useState(() => new InputState(onSave, name, validation));
        const wrapperClass = state.isValid ? 'element hasLabel' : 'element hasLabel error invalid';

        return (
            <div
                className={wrapperClass}
                data-test={dataTest}
            >
                <input
                    type='text'
                    name={name}
                    defaultValue={isCache && isNaN(toNumber(value)) === false ? toNumber(value) / 100 : value}
                    onBlur={state.onSave}
                />
                <label>{label}</label>
                {state.isValid ? null : (
                    <div className='error-message'>
                        <div className='error-message-content'>
                            Value must be between [{validation?.details.min}-{validation?.details.max}]
                        </div>
                    </div>
                )}
            </div>
        );
    }
);

type CheckboxPropsType = InputPropsType & { checked: boolean };

export const Checkbox = observer(
    'MarketTemplateCheckbox',
    ({ dataTest, label, name, onSave, checked }: CheckboxPropsType) => {
        const [state] = useState(() => new InputState(onSave, name));
        return (
            <div
                className='element checkbox'
                data-test={dataTest}
            >
                <label>
                    <input
                        type='checkbox'
                        defaultChecked={checked}
                        onChange={state.onSave}
                    />
                    <span>{label}</span>
                </label>
            </div>
        );
    }
);
